import { memo, useCallback, useMemo, useRef } from 'react'
import { WheelPicker as LibWheelPicker, WheelPickerWrapper } from '@ncdai/react-wheel-picker'

import { useHaptic } from '~/shared/platform'

import { WHEEL_ITEM_HEIGHT_PX, WHEEL_VISIBLE_COUNT } from './config'
import type { WheelPickerGroupProps, WheelPickerProps } from './types'

import '@ncdai/react-wheel-picker/style.css'
import styles from './WheelPicker.module.scss'

/**
 * Группа барабанов — общая для всех колонок пикера.
 *
 * Обязательна: библиотека раскладывает колонки внутри одной обёртки-флексбокса и от неё же
 * берёт высоту и центр окна выбора. Обернуть каждую колонку отдельно нельзя — окна выбора
 * разъедутся по вертикали.
 *
 * Пропорции колонок (день / месяц / год = 1 / 1.6 / 1.2 по макету A2·2) задаются через
 * `flexes`: компоненты библиотеки не принимают ни `style`, ни класс на корень колонки,
 * поэтому ширины раздаём CSS-переменными по порядку детей.
 *
 * Высоту зоны библиотека вычисляет сама из `optionItemHeight` и `visibleCount` и пишет её
 * инлайном — снаружи она не задаётся, регулируется только этими двумя числами в config.ts.
 */
export const WheelPickerGroup = memo(function WheelPickerGroup(props: WheelPickerGroupProps) {
  const { children, flexes } = props

  const cssVars = useMemo(
    () =>
      ({
        '--wheel-item-height': `${WHEEL_ITEM_HEIGHT_PX}px`,
        ...Object.fromEntries(
          (flexes ?? []).map((flex, index) => [`--wheel-flex-${index + 1}`, String(flex)]),
        ),
      }) as React.CSSProperties,
    [flexes],
  )

  return (
    // data-vaul-no-drag — барабан двигает пальцем сам (без нативного скролла), и BottomSheet
    // не должен перехватывать этот жест как свайп-закрытие. Ставим на обёртку, а не на сам
    // WheelPickerWrapper: у него из пропсов есть только className, произвольный атрибут через
    // него не прокинуть, а data-vaul-no-drag ищет ближайший подходящий предок через `closest()`.
    <div className={styles.group} style={cssVars} data-vaul-no-drag>
      <WheelPickerWrapper className={styles.wrapper}>{children}</WheelPickerWrapper>
    </div>
  )
})

/**
 * Одна колонка барабана (спека 01 §7). Рендерится только внутри `WheelPickerGroup`.
 *
 * Под капотом — `@ncdai/react-wheel-picker`: колесо с 3D-перспективой, инерцией и снапом.
 * Свою реализацию на scroll-snap убрали: она не давала инерции и плохо ощущалась пальцем,
 * а барабан здесь главный способ ввода даты и времени.
 *
 * Наружу компонент остаётся нашим: `options` / `value` / `onChange`, никаких className.
 */
export const WheelPicker = memo(function WheelPicker(props: WheelPickerProps) {
  const { options, value, onChange } = props
  const haptic = useHaptic()

  const libOptions = useMemo(
    () => options.map((option) => ({ value: option.value, label: option.label })),
    [options],
  )

  // Отклик на смену значения — «щелчок» настоящего барабана (правка основателя).
  // selectionChanged, а не impact: это ровно тот системный отклик, которым iOS озвучивает
  // прокрутку пикеров, и он самый тихий из трёх.
  //
  // Повторы гасим вручную. Библиотека дёргает onValueChange дважды за бросок — сразу после
  // отрыва пальца и ещё раз в конце инерции; без этой проверки на один жест приходила пара
  // импульсов подряд. Стартовое значение в ref — чтобы не щёлкнуть на первом рендере
  // и на значении, выставленном снаружи.
  const lastBuzzedRef = useRef(value)

  const handleChange = useCallback(
    (next: string) => {
      if (next !== lastBuzzedRef.current) {
        lastBuzzedRef.current = next
        haptic.selection()
      }
      onChange(next)
    },
    [haptic, onChange],
  )

  return (
    <LibWheelPicker
      options={libOptions}
      value={value}
      onValueChange={handleChange}
      optionItemHeight={WHEEL_ITEM_HEIGHT_PX}
      visibleCount={WHEEL_VISIBLE_COUNT}
      classNames={{
        optionItem: styles.option,
        highlightWrapper: styles.highlight,
      }}
    />
  )
})
